import { useEffect, useRef, useState, useCallback } from "react";

import { PaymentComponentInstance, PaymentStatus } from "@bookingcom/payment-component";
import { stage } from "@flights/et-universal";
import { flightsSessionStore, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { getCurrencySymbolByPointOfSale } from "@flights/content-universal";

import useGlobalContext from "./useGlobalContext";
import useContextName from "./useContextName";
import useEvents from "./useEvents";
import useClientMetrics from "./useClientMetrics";
import { FLOW_TYPE_SUPPLIER_3DS } from "../constants";
import { UIPaymentComponentProductType, UIPointOfSale } from "@flights/types";
import useUserAgent from "./useUserAgent";
import usePointOfSale from "./usePointOfSale";

import { actions as PaymentLoadingStatusActions } from "../store/paymentLoadingStatus/actions";
import { actions as LoaderActions } from "../store/loadingScreen/actions";
import { LoadingStep } from "../store/paymentLoadingStatus/state";
import { useDispatch, useSelector } from "react-redux";
import { getOrder } from "../store/order/selectors";

type PaymentComponentHook = {
  paymentId: string;
  successfulCallback: () => Promise<any>;
  pendingCallback: () => Promise<any>;
  failedCallback: () => Promise<any>;
  loadingFailedCallback?: () => void;
  paymentComponentProductType?: UIPaymentComponentProductType;
  orderId: string;
  type?: "AddOnOrder" | "order";
};

export const usePaymentComponent = ({
  paymentId,
  successfulCallback,
  pendingCallback,
  failedCallback,
  loadingFailedCallback,
  paymentComponentProductType,
  orderId,
  type
}: PaymentComponentHook) => {
  const { env, locale, requestId, ipCountry, isInternal } = useGlobalContext();
  // eslint-disable-next-line no-restricted-syntax -- this line was auto generated, hence fix the issue timely
  const pcContainerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line no-restricted-syntax -- this line was auto generated, hence fix the issue timely
  const refPC = useRef<PaymentComponentInstance | null>(null);
  const [pcIsLoading, setPCLoading] = useState(false);
  const [pcEnableLoader, setPCEnableLoader] = useState(false);
  const [pcPaymentMethod, setPCPaymentMethod] = useState<string | undefined>(undefined);
  const [pcIsLoaded, setPCLoaded] = useState(false);
  const [pcStatus, setPCStatus] = useState<PaymentStatus | "initial" | "processing">("initial");
  const { trackClientMetric } = useClientMetrics();
  const contextName = useContextName();
  const { jsError, grumble } = useEvents();
  const pointOfSale = usePointOfSale() as UIPointOfSale;

  const order = useSelector(getOrder);
  const supplierType = order?.supplierType;

  const dispatch = useDispatch();

  const { isMobile } = useUserAgent();

  const qs = new URLSearchParams(location.search);
  const paymentRedirectId = qs.get("pid");
  const isSupplier3DSFlow = qs.get("flowType") === FLOW_TYPE_SUPPLIER_3DS;
  const isPaymentRedirect = paymentRedirectId && qs.get("client") === "payment_component" && qs.has("result");

  const [pcInputValid, setPCInputValid] = useState<boolean | undefined>(undefined);

  const handlePendingPayment = useCallback(
    (status: PaymentStatus) => {
      trackClientMetric("pc_cb_pending");
      grumble("payment pending called", { contextName, requestId, status });
      pendingCallback()
        .then(() => {
          // Let to handle the pending first (for instance, queue a refund),
          // and only then update the status
          setPCStatus(status);
        })
        .catch((err) => {
          jsError(err);
          setPCStatus(status);
        });
    },
    [trackClientMetric, pendingCallback, grumble, jsError, contextName, requestId]
  );

  const pcProcessPayment = useCallback(
    (pcLoadingStepsEnabled?: boolean) => {
      if (refPC.current) {
        if (pcLoadingStepsEnabled) {
          dispatch(PaymentLoadingStatusActions.setLoadingStep(LoadingStep.VERIFYING_PAYMENT));

          if (supplierType === "AGENCY") {
            dispatch(LoaderActions.show());
          }
        }
        setPCStatus("processing");
        refPC.current.process();
      } else {
        throw new Error("Payment component failed to initialize");
      }
    },
    [dispatch, supplierType]
  );

  const pcTriggerValidation = useCallback(() => {
    if (refPC.current) {
      refPC.current.triggerValidation();
    } else {
      throw new Error("Payment component failed to initialize");
    }
  }, []);

  const pcChangePaymentId = useCallback((newPaymentId: string) => {
    if (refPC.current) {
      refPC.current.changePaymentId(newPaymentId);
    } else {
      throw new Error("Payment component failed to initialize");
    }
  }, []);

  const pcShowError = useCallback((error: string) => {
    if (refPC.current) {
      refPC.current.showError(error);
    } else {
      throw new Error("Payment component failed to initialize");
    }
  }, []);

  useEffect(() => {
    if (process.env.BUILD_TARGET !== "client") return;
    if (isPaymentRedirect || isSupplier3DSFlow) return; // skip loading payment component if this is a redirect after 3ds
    if (refPC.current) return; // skip loading payment component if the ref is not null

    import(/* webpackChunkName: "payment-component" */ "@bookingcom/payment-component")
      // eslint-disable-next-line @typescript-eslint/naming-convention -- this line was auto generated, hence fix the issue timely
      .then(({ default: PaymentComponent }) => {
        if (pcContainerRef.current && paymentId) {
          if (refPC.current) {
            jsError(new Error("PC would render twice"));
          } else {
            setPCLoading(true);
            trackClientMetric("pc_init");
            const pcInitTimestamp = Date.now();
            const iAmFrom = isInternal ? ipCountry : undefined;
            const browsingCurrency = isInternal
              ? getCurrencySymbolByPointOfSale(
                  // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
                  ipCountry || "",
                  isFeatureRunningClientSide("FLIGHTS_WEB_CAT_BG_EUR_CURRENCY")
                )
              : undefined;

            refPC.current = PaymentComponent.create(pcContainerRef.current, {
              paymentId,
              productCode: paymentComponentProductType || "FLIGHTS",
              isDevEnv: env !== "prod",
              lang: locale,
              iAmFrom,
              browsingCurrency,
              userPlatform: isMobile ? "MDOT" : "WWW",
              onLoad: () => {
                setPCLoading(false);
                setPCLoaded(true);
                trackClientMetric("pc_load_ok");
                trackClientMetric("pc_load_time", Date.now() - pcInitTimestamp);
                if (pointOfSale === "us") {
                  stage("flights_us_mor_web", 7);
                }
                const agencyPaymentError = flightsSessionStore.get("AGENCY_PAYMENT_ERROR");
                if (!!agencyPaymentError) {
                  pcShowError(agencyPaymentError);
                }
              },
              onLoadError: () => {
                setPCLoading(false);
                loadingFailedCallback && loadingFailedCallback();
                jsError(new Error("PC failed to load"));

                /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
                /* TODO(asamilyak): test on prod and add a manual timeout check
                    if `onLoadError` callback won't be properly fired by PC
                    in all cases we need (e.g. on load timeout) */
                trackClientMetric("pc_load_nok");
                trackClientMetric("pc_load_error");
              },
              onPaymentExpired: () => {
                setPCLoading(false);
                //if payment expired, we handle it just like a failed callback, but we report a separate error.
                loadingFailedCallback && loadingFailedCallback();
                jsError(new Error("PC expired"));
                grumble("payment expired called", { contextName, requestId, status });
                trackClientMetric("pc_load_nok");
                trackClientMetric("pc_payment_expired");
              },
              onPaymentSuccessful: (status) => {
                if (status === "pc-payment-pending") {
                  handlePendingPayment(status);
                  return;
                }
                grumble("payment successful called", { contextName, requestId, status });
                setPCStatus(status);
                trackClientMetric("pc_cb_success");
                void successfulCallback();
              },
              onPaymentFailed: (status) => {
                if (status === "pc-payment-pending") {
                  handlePendingPayment(status);
                  return;
                }
                if (status === "pc-validation-failed") {
                  // this is a client side validation of the card
                  trackClientMetric("pc_cb_validation_failed");
                } else {
                  grumble("payment failed called", { contextName, requestId, status });
                  trackClientMetric("pc_cb_failed");
                }
                setPCStatus(status);
                void failedCallback();
              },
              onChallengeLoadSuccessful: () => {
                dispatch(LoaderActions.hide());
                grumble("challenge load successful", { orderId });
              },
              onChallengeLoadFailed: () => {
                grumble("challenge load failed", { orderId });
              },
              onInvalidFields: () => {
                setPCInputValid(false);
              },
              onNoInvalidFields: () => {
                setPCInputValid(true);
              },
              onUiUpdate: ({ enableLoader }) => {
                if (!enableLoader) {
                  dispatch(LoaderActions.hide());
                }
                setPCEnableLoader(enableLoader);
              },
              onUserTappedPaymentMethod: (_, newPaymentMethod) => {
                setPCPaymentMethod(newPaymentMethod.name);
              }
            });
          }
        }
      })
      .catch((err) => jsError(err));
  }, [
    env,
    locale,
    setPCLoaded,
    setPCLoading,
    trackClientMetric,
    isPaymentRedirect,
    paymentId,
    isSupplier3DSFlow,
    loadingFailedCallback,
    successfulCallback,
    failedCallback,
    paymentComponentProductType,
    handlePendingPayment,
    contextName,
    requestId,
    ipCountry,
    isInternal,
    grumble,
    jsError,
    orderId,
    isMobile,
    pointOfSale,
    setPCInputValid,
    pcShowError,
    dispatch
  ]);

  useEffect(() => {
    if ((isPaymentRedirect && pcStatus === "initial" && paymentId === paymentRedirectId) || isSupplier3DSFlow) {
      isPaymentRedirect
        ? grumble("successful payment redirect flow", { paymentId })
        : grumble("successful supplier 3ds flow", { paymentId });
      setPCStatus("processing");
      trackClientMetric("pc_cb_redirect_success");
      void successfulCallback();
    } else if (isPaymentRedirect) {
      grumble("Something went wrong with payment redirect", {
        isPaymentIdAligned: paymentId === paymentRedirectId,
        pcStatus,
        paymentId,
        paymentRedirectId,
        type
      });
    }
  }, []); // eslint-disable-line

  return {
    pcContainerRef,
    pcProcessPayment,
    pcStatus,
    pcIsLoading,
    pcIsLoaded,
    pcTriggerValidation,
    pcChangePaymentId,
    pcShowError,
    pcInputValid,
    pcEnableLoader,
    pcPaymentMethod
  };
};
